import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import { Checkbox, FormControlLabel, Grid,  Paper, TableContainer,
  Table, TableHead, TableCell, TableBody, TableRow, TextField, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CancelCircleOutline from '@mui/icons-material/Cancel';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SaveButton from '../ui/SaveButton';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';

import PayoutChargeRecalcQuery from './PayoutChargeRecalcQuery';
import PayoutDetailUpdateMutation from './PayoutDetailUpdateMutation';

import { red, green } from '@mui/material/colors';
import { endOfDay, format, subDays } from 'date-fns';

const abEnvironment = new AbEnvironment();
const PayoutDetailQuery = graphql`
query PayoutDetailQuery($vendorPayoutId: UUID!) {
  vendorPayoutTByVendorPayoutId(vendorPayoutId: $vendorPayoutId) {
    resellerAccountId
    accountTByResellerAccountId {
      accountName
    }
    autoPayout
    approvedTime
    approvedBy
    statusReason
    status
    chargeAmountTotal
    payoutAmount
    calculationAudit
    created
    startTs
    endTs
    programType
    userTByApprovedBy {
      firstName
      lastName
    }
    resellerPId
    resellerProgramTByResellerPId {
      programType
      programName
      description
      customerAppId
      qualificationPeriod
    }
    vendorPayoutChargeTsByVendorPayoutId {
      nodes {
        status
        chargeAmount
        payoutAmount
        customerAppTByCustomerAppId {
          customerAccountId 
          accountTByCustomerAccountId {
            accountName
          }
          appTByAppId {
            appName
            appSku
          }
        }
        externalSubscriptionTByExternalSubscriptionId {
          customerAccountId 
          accountTByCustomerAccountId {
            accountName
          }
          appTByAppId {
            appName
            appSku
          }
        }
        created
        updated
        chargeTransactionId
        transactionTByChargeTransactionId {
          transactionTime
        }
        externalChargeTransactionId
        externalSubscriptionPaymentTByExternalChargeTransactionId {
          transactionTime
        }
        vendorPayoutChargeId
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary,
    maxWidth: 1200,
  },
  detailIcon: {
    width: 22,
    height: 22,
  }
});

class PayoutDetail extends Component {

  constructor(props) {
    super(props);

    this.state = {
      //this is null until an approval change
      detailCharges: null,
      chargeAmountTotal: null,
      payoutAmount: null,

      changeMade: false,
      showSpinner: false,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null
    };  
 
  }
  propDetailCharges = null;
  resellerPId = null;

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) {  window.analytics.track('Approving a payout'); }
  }

  // Get lines.  If line state is null, this means nothing has changed from the initial render.  So props
  // is the source of truth.  However, once something changes, state is the source of truth.  This method is
  // a convenience to return state if state exists, and props if it doesn't.
  getDetailCharges() {
    if (this.state.detailCharges != null)
      return this.state.detailCharges;

    if (this.propDetailCharges != null)
      return this.propDetailCharges;

    return null;
  }
  
  handleApprovalChange = name => event => {

    const { logError } = this.props.auth;

    let detailCharges = this.getDetailCharges();
    let newTotalCharges = Number.parseFloat(0);
    let newDetailCharges = [];
    for (let i = 0; i < detailCharges.length; i++) {
      let charge = detailCharges[i];
      //clone original
      let chargeClone = JSON.parse(JSON.stringify(charge));
      if (charge.vendorPayoutChargeId === event.target.name) {
        if (event.target.checked) {
          chargeClone.status = 1;
          
        } else {
          chargeClone.status = 5;
        }
        chargeClone.statusChanged = 1;
        newDetailCharges.push(chargeClone);
      } else {
        newDetailCharges.push(chargeClone);
      }

    }
    for (let i = 0; i < newDetailCharges.length; i++) {
      let charge = newDetailCharges[i];
      if (charge.status === 1) {
        newTotalCharges = newTotalCharges + Number.parseFloat(charge.chargeAmount);
      } 
    }

    this.setState({changeMade: true, detailCharges: newDetailCharges, payoutAmount: "Recalculating", chargeAmountTotal: newTotalCharges});

    //make a call to backend to determine new payout amount
    PayoutChargeRecalcQuery.fetch(this.resellerPId, newTotalCharges, newTotalCharges).toPromise()
    .then(response => {
      this.setState({payoutAmount: response.calculatevendorpayout});
    })
    .catch(error => {
      logError('Exception in PayoutDetail.js: ' + error);
    });

  }

  getProgramTypeText(type) {

    if (type === 0) {
      return "Instant rebate";
    } else if (type === 1) {
      return "Monthly rebate";
    } else if (type === 2) {
      return "Monthly credits";
    } else if (type === 3) {
      return "Prepaid credits";
    } else if (type === 4) {
      return "One-time rebate"
    } else {
      return "Unknown";
    }
  }

  getQualificationPeriodText(period) {

    if (period === 0) {
      return "Monthly";
    } else if (period === 1) {
      return "total cumulative";
    } else {
      return "Unknown";
    }
  }

  getProgramStatusText(status) {

    if (status === 0) {
      return "pending";
    } else if (status === 1) {
      return "approved";
    } else if (status === 2) {
      return "transferring from vendor";
    } else if (status === 3) {
      return "transferring to reseller";
    } else if (status === 4) {
      return "paid";
    } else if (status === 5) {
      return "not approved";
    } else if (status === 6) {
      return "reversed";
    } else if (status === 12) {
      return "error transferring from vendor";
    } else if (status === 13) {
      return "error transferring to reseller";
    } else {
      return "Unknown";
    }
  }

  render() {
    const { isAuthenticated, isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const okColor = green[300];
    const redColor = red[600];
    
    const params = new URLSearchParams(this.props.location.search);
    let vendorPayoutId = params.get('vendorPayoutId');
    let fp = params.get('fp');
    let backTo = "Payouts";
    if (fp != null && fp !== '') {
      backTo = fp;
    }
 
    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    } else if (isAuthenticatedVendor()) {
      return (
        <Grid container spacing={3} direction="row" justifyContent="flex-start">
          <Grid item xs={12}>
            <PageTitle title="Payout details" subtitle="View payout details and review / approve individual subscription eligibility." showBackButton={true} name={backTo} noMargin={true} history={this.props.history}/>
          </Grid>
          <Grid item xs={12}>
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={PayoutDetailQuery}
              variables={{vendorPayoutId: vendorPayoutId}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {

                  // Now that we have props from the query, we can setup program lines for rendering.  But if state has been
                  // set, we should use the state values.  We'll determine which to use through a convenience method.
                  if (props.vendorPayoutTByVendorPayoutId && props.vendorPayoutTByVendorPayoutId.vendorPayoutChargeTsByVendorPayoutId && props.vendorPayoutTByVendorPayoutId.vendorPayoutChargeTsByVendorPayoutId.nodes) {
                    this.propDetailCharges = props.vendorPayoutTByVendorPayoutId.vendorPayoutChargeTsByVendorPayoutId.nodes;
                  }
                  this.resellerPId = props.vendorPayoutTByVendorPayoutId.resellerPId;

                  let detailCharges = this.getDetailCharges();

                  let totalCharge = null;
                  if (this.state.chargeAmountTotal !== null) {
                    if (this.state.chargeAmountTotal === 'Recalculating') {
                      totalCharge = this.state.chargeAmountTotal
                    } else {
                      totalCharge = Number(this.state.chargeAmountTotal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                    }
                  } else {
                    totalCharge = Number(props.vendorPayoutTByVendorPayoutId.chargeAmountTotal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                  }

                  let totalEarned = null;
                  if (this.state.payoutAmount) {
                    if (this.state.payoutAmount === 'Recalculating') {
                      totalEarned = this.state.payoutAmount
                    } else {
                      totalEarned = Number(this.state.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                    }
                  } else {
                    if (props.vendorPayoutTByVendorPayoutId.status === 6) {
                      totalEarned = Number(0).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                    } else {
                      totalEarned = Number(props.vendorPayoutTByVendorPayoutId.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                    }
                  }

                  //periodStart is fine as is, period end should be end of the month;
                  // most "endTs" are start of next month 0hr, adjust to "minus day, to end of day"
                  let periodStart = format(new Date(props.vendorPayoutTByVendorPayoutId.startTs), 'yyyy-MM-dd');
                  let periodEnd = format(endOfDay(subDays(new Date(props.vendorPayoutTByVendorPayoutId.endTs), 1)), 'yyyy-MM-dd');
                  let created = format(new Date(props.vendorPayoutTByVendorPayoutId.created), 'yyyy-MM-dd');

                  let approvedTime = '';
                  let approvedDate = '';
                  if (props.vendorPayoutTByVendorPayoutId.approvedTime) {
                    approvedTime = format(new Date(props.vendorPayoutTByVendorPayoutId.approvedTime), 'HH:mm:ss');
                    approvedDate = format(new Date(props.vendorPayoutTByVendorPayoutId.approvedTime), 'yyyy-MM-dd');
                  } else {
                    //auto approved 
                    approvedTime = format(new Date(props.vendorPayoutTByVendorPayoutId.created), 'HH:mm:ss');
                    approvedDate = format(new Date(props.vendorPayoutTByVendorPayoutId.created), 'yyyy-MM-dd');
                  }
                  let approvedBy = '';
                  if (props.vendorPayoutTByVendorPayoutId.approvedBy) {
                    if (props.vendorPayoutTByVendorPayoutId.userTByApprovedBy) {
                      approvedBy = props.vendorPayoutTByVendorPayoutId.userTByApprovedBy.firstName + ' ' + props.vendorPayoutTByVendorPayoutId.userTByApprovedBy.lastName;
                    } 
                  }

                  let programName = null;
                  let description = null;
                  if (props.vendorPayoutTByVendorPayoutId.resellerProgramTByResellerPId != null) {
                    programName = props.vendorPayoutTByVendorPayoutId.resellerProgramTByResellerPId.programName;
                    description = props.vendorPayoutTByVendorPayoutId.resellerProgramTByResellerPId.description;
                  }
                  
                  let rowCount = 0;

                  // Derive the detail message
                  let detailMessage = '';
                  let status        = props.vendorPayoutTByVendorPayoutId.status;
                  let autoPayout    = props.vendorPayoutTByVendorPayoutId.autoPayout;
                  let statusReason  = props.vendorPayoutTByVendorPayoutId.statusReason;
                  let reverseReason = statusReason;
                  if (!reverseReason) {
                    reverseReason = 'The subscription charge was refunded.';
                  }

                  if (props.vendorPayoutTByVendorPayoutId.programType === 0) {
                    detailMessage = detailMessage = 'This payout was generated and automatically approved on ' + created + ' for purchases that occurred on ' + periodStart + '.';
                  } else if (props.vendorPayoutTByVendorPayoutId.programType === 5) {
                    detailMessage = detailMessage = 'This one-time payout was generated and approved on ' + created + '.';
                    if (statusReason) {
                      detailMessage = detailMessage + ' Reason: ' + statusReason;
                    }
                  } else {
                    if (autoPayout === 0) {
                      //common text to all messages; additional line += to include extra status info.
                      detailMessage = 'This payout was generated on ' + created + ' and covers the period from ' + periodStart + ' through ' + periodEnd + '.';
                      switch(status) {
                        case 1:
                        case 2:
                        case 3:
                        case 4:
                          detailMessage+= '  It was approved by ' + approvedBy + ' on ' + approvedDate + ' at ' + approvedTime + '.';
                          break;
                        case 5:
                          detailMessage+= '  It was rejected by ' + approvedBy + ' on ' + approvedDate + ' at ' + approvedTime + '. Reason: ' + props.vendorPayoutTByVendorPayoutId.statusReason;
                          break;
                        case 6:
                          detailMessage+= '  It was approved by ' + approvedBy + ' on ' + approvedDate + ' at ' + approvedTime + '. This payout was subsequently reversed for this reason: ' + reverseReason;
                          break;
                        case 0:
                        default:
                          detailMessage+= '  It is in a ' + this.getProgramStatusText(status) + ' status.';
                          break;
                      }
                    } else {
                      //common text to all messages; additional line += to include extra status info.
                      detailMessage = 'This payout was generated and automatically approved on ' + created + ' and covers the period from ' + periodStart + ' through ' + periodEnd + '.';
                      switch(status) {
                        case 0:
                        case 1:
                        case 2:
                        case 3:
                        case 4:
                          //unmodified, just the basic message.
                          break;
                        case 5:
                        case 6:
                          detailMessage+= ' This payout was subsequently reversed for this reason: ' + reverseReason;
                          break;
                        default:
                          detailMessage+= ' It is in a ' + this.getProgramStatusText(status) + ' status.';
                          break;
                        }
                    }
                  }

                  return ( 
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="flex-start" style={{marginBottom: '10px'}}>
                        <Grid item xs={12}>
                          <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                            {'Payout for ' + props.vendorPayoutTByVendorPayoutId.accountTByResellerAccountId.accountName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            {detailMessage}
                          </Typography>
                        </Grid>
                        {programName != null &&
                        <Grid item xs={12}>
                          <Typography variant="body1">
                          {'Commission program: ' + programName + ' (' + description + ')'}
                          </Typography>
                        </Grid>}

                        {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                        <React.Fragment>
                          <Grid item xs={12}>
                            <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                              Totals for period
                            </Typography>
                          </Grid>
                          <Grid item >
                            <TextField
                                id="charge-amount" label={'Total charge amount'}
                                value={ totalCharge }
                                InputProps={{ readOnly: true, disableUnderline: true }} 
                                />
                          </Grid>
                          <Grid item>
                            <TextField
                                id="payout-amount" label={'Payout amount'}
                                value={ totalEarned }
                                InputProps={{ readOnly: true, disableUnderline: true }} 
                                />
                          </Grid>
                          {props.vendorPayoutTByVendorPayoutId.calculationAudit &&
                            <Grid item xs={12}>
                              <Typography variant="body1">
                                {'Audit: ' + props.vendorPayoutTByVendorPayoutId.calculationAudit}
                              </Typography>
                            </Grid>
                          
                          }                        
                          <Grid item xs={12}>
                            <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                              Details
                            </Typography>
                          </Grid>
                        </React.Fragment>}

                        <Grid>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                  <TableCell>Customer name</TableCell>}
                                  {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                  <TableCell>App name</TableCell>}
                                  <TableCell>Transaction time</TableCell>
                                  {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                  <TableCell align="right">Charge amount</TableCell>}
                                  <TableCell align="right">Payout amount</TableCell>
                                  <TableCell>Approved</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>

                                {detailCharges.map(n => {

                                  let chargeAmount = Number(n.chargeAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                                  let payoutAmount = Number(n.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                                  let transactionTime = null;
                                  let theApp = null;
                                  let accountName = null;    
                                  if (props.vendorPayoutTByVendorPayoutId.programType === 4) {
                                    transactionTime = format(new Date(n.externalSubscriptionPaymentTByExternalChargeTransactionId.transactionTime), 'yyyy-MM-dd HH:mm:ss');
                                    theApp = n.externalSubscriptionTByExternalSubscriptionId.appTByAppId;
                                    accountName = n.externalSubscriptionTByExternalSubscriptionId.accountTByCustomerAccountId.accountName;
                                  } else if (props.vendorPayoutTByVendorPayoutId.programType === 5) {
                                    transactionTime = format(new Date(n.created), 'yyyy-MM-dd HH:mm:ss');
                                    theApp = '';
                                    accountName = 'Some account';
                                  } else {
                                    transactionTime = format(new Date(n.transactionTByChargeTransactionId.transactionTime), 'yyyy-MM-dd HH:mm:ss');
                                    theApp = n.customerAppTByCustomerAppId.appTByAppId;
                                    accountName = n.customerAppTByCustomerAppId.accountTByCustomerAccountId.accountName;
                                  }

                                  let appName = theApp.appName;
                                  if (theApp.appSku && theApp.appSku !== '') {
                                    appName = appName + " (" + theApp.appSku + ")";
                                  }

                                  return (
                                    <TableRow key={rowCount++}>
                                      {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                      <TableCell>{accountName}</TableCell>}
                                      {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                      <TableCell>{appName}</TableCell>}
                                      <TableCell>{transactionTime}</TableCell>
                                      {props.vendorPayoutTByVendorPayoutId.programType !== 5 &&
                                      <TableCell align="right">{chargeAmount}</TableCell>}
                                      <TableCell align="right">{payoutAmount}</TableCell>
                                      {props.vendorPayoutTByVendorPayoutId.status === 0 &&
                                      <TableCell align="center">
                                        <FormControlLabel
                                          control={
                                            <Checkbox name={n.vendorPayoutChargeId} checked={n.status === 1} onChange={this.handleApprovalChange(n.vendorPayoutChargeId, props)}  />
                                          }
                                          
                                        />
                                      </TableCell>
                                      }
                                      {props.vendorPayoutTByVendorPayoutId.status !== 0 &&
                                      <TableCell align="center">
                                        {n.status === 1 &&
                                          <CheckCircle htmlColor={okColor}/>
                                        }
                                        {n.status === 5 &&
                                          <CancelCircleOutline htmlColor={redColor}/>
                                        }
                                      </TableCell>
                                      }

                                    </TableRow>
                                  );
                                })}
                                </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={12}>
                          <SaveButton
                                snackbarOpen={this.state.saveSnackbarOpen}
                                autoHideDuration={this.state.saveSnackbarDuration}
                                status={this.state.saveStatus} 
                                message={this.state.saveMessage}
                                disabled={this.state.changeMade === false}
                                onSaveClick={() => {this._updatePayout(vendorPayoutId, this.resellerPId)}}
                                onSnackbarClose={() => { this._onSnackbarClose(); }}
                              />
                        </Grid>
                      </Grid>
                    </Paper>
                  )
                }
                return (
                  <div>
                    <Typography>Loading...</Typography>
                    <LoadingSpinner/>
                  </div>
                );
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }

  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  _updatePayout = (vendorPayoutId, resellerPId) => {
    this.setState({ saveStatus: 'saving' });

    let vendorAccountId = abEnvironment.getUserAttribute("a_id");
    let detailCharges = this.state.detailCharges;
    let reformattedDetailCharges = [];
 
    //the mutation wants the detail in a slightly different format.
    for (var i = 0; i < detailCharges.length; i++) {
      let detailCharge = detailCharges[i];
      reformattedDetailCharges.push({"vendorPayoutChargeId": detailCharge.vendorPayoutChargeId, 
                                      "status": detailCharge.status, 
                                      "chargeAmount": detailCharge.chargeAmount,
                                      "statusChanged": (detailCharge.statusChanged ? detailCharge.statusChanged: 0)})

    }
            
    PayoutDetailUpdateMutation.commit(vendorAccountId, vendorPayoutId, resellerPId, reformattedDetailCharges)
    .then(response => {
      this.setState({ changeMade: false, showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Approvals saved.', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Unable to save approvals', saveSnackbarDuration: null });
    });

   }

}

PayoutDetail.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(PayoutDetail);
